export interface ControlSchedule {
  type: 'day' | 'hour' | 'day+hour';
  slots: Array<{
    date?: string;       // YYYY-MM-DD (si day ou day+hour)
    startTime?: string;  // HH:mm (si hour ou day+hour)
    endTime?: string;    // HH:mm (si hour ou day+hour)
  }>;
}

export interface TplSource {
  _id: string;
  name: string;
  prixVente?: number;
  draft?: boolean;
  /** 'garbage' = tarif supprimé (tarifs en ligne et d'import). */
  statut?: string;
  /** 'garbage' = tarif supprimé (tarifs sur place, qui portent `status` et non `statut`). */
  status?: string;
  ticketCount?: number;
  controlLabel?: string;
  controlColor?: string;
  idTplGroup?: string | null;
  idGroup?: string | null;
  hidden?: boolean;
  sortInGroup?: number;
  controlSchedule?: ControlSchedule | null;
}

export interface TplGroup {
  _id: string;
  name: string;
  color: string;
  sort: number;
}

export interface EventData {
  _id: string;
  name: string;
  /** « DD/MM/YYYY HH:mm » */
  startDate?: string;
  /** « DD/MM/YYYY HH:mm » */
  endDate?: string;
  /** 'clotured' = billetterie clôturée. */
  status?: string;
  /** true = événement « en stock » (pas encore configuré pour la vente). */
  first?: boolean;
  /** Épinglé par l'organisateur dans le BO Online. */
  favorite?: boolean;
  tplOnline: TplSource[];
  tplOnsite: TplSource[];
  tplImport: TplSource[];
  tplGroups?: TplGroup[];
}

export interface TplConfig {
  checked: boolean;
  scanNbr: number;
  infinity: boolean;
  nfcMode: string;
  colorConfirmation: string; // '' = vert par défaut (non persisté), sinon hexa
  /** Type d'accès géré par Wilout Backstage (#9400) : affiché, jamais modifié ni envoyé par BO Contrôle. */
  accreditation: boolean;
  original: {checked: boolean; scanNbr: number; infinity: boolean; nfcMode: string; colorConfirmation: string};
}

/** Couleur par défaut de la popup de validation (verte) — non persistée dans le doc liste. */
export const DEFAULT_CONFIRMATION_COLOR = '#00b797';

/** Palette des couleurs de confirmation sélectionnables par tarif. */
export const CONFIRMATION_COLORS: ReadonlyArray<{name: string; value: string}> = [
  {name: 'Vert (par défaut)', value: DEFAULT_CONFIRMATION_COLOR},
  {name: 'Bleu', value: '#2d8bca'},
  {name: 'Bleu foncé', value: '#2d54d3'},
  {name: 'Violet', value: '#7d33d1'},
  {name: 'Magenta', value: '#c041c0'},
  {name: 'Jaune', value: '#e0b90b'},
];

// #4509/#5563 : plus de source 'import' — les tarifs importés sont fusionnés dans 'online'.
export type SourceType = 'online' | 'onsite';

export interface SourceBlock {
  type: SourceType;
  label: string;
  icon: string;
  templates: TplSource[];
  emptyMessage: string;
}

export interface EventWithSources extends EventData {
  sources: SourceBlock[];
}

export interface TemplateGroup {
  _id: string | null;
  name: string;
  color: string;
  isDefault: boolean;
  templates: TplSource[];
}

const COLOR_MAP: Record<string, string> = {
  teal: 'var(--ui-color-teal-400)',
  green: 'var(--ui-color-green-400)',
  blue: 'var(--ui-color-blue-400)',
  indigo: 'var(--ui-color-indigo-400)',
  purple: 'var(--ui-color-purple-400)',
  magenta: 'var(--ui-color-pink-400)',
  red: 'var(--ui-color-red-400)',
  orange: 'var(--ui-color-orange-400)',
  yellow: 'var(--ui-color-yellow-400)',
  neutral: 'var(--ui-color-neutral-400)',
  violet: 'var(--ui-color-purple-400)',
  cyan: 'var(--ui-color-teal-300)',
  darkgreen: 'var(--ui-color-green-600)',
  pink: 'var(--ui-color-pink-400)',
};

export function colorToVar(color: string): string {
  if (!color) return '';
  if (color.startsWith('#') || color.startsWith('rgb')) return color;
  return COLOR_MAP[color] || `var(--ui-color-${color})`;
}

/** Selection state predicates shared by group / source / event checkboxes. */
export const allSelected = (checked: number, total: number): boolean => total > 0 && checked === total;
export const someSelected = (checked: number, total: number): boolean => checked > 0 && checked < total;
